import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    DialogProps,
    IconButton,
    IconButtonProps,
    Dialog as MuiDialog,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useContext } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { mutationCreateUser, mutationUpdateUser } from "src/api/user";
import { SettingContext } from "src/contexts/settings";
import { Type_User } from "src/types/user";
import { createUserSchema } from "src/validators/createUser";

type Type_Props_Create_User = {
    close: () => void;
};

type Type_Create_User_Data = {
    email: string;
    password: string;
    confirmPassword: string;
};

const Icon = styled(IconButton)<IconButtonProps>(() => ({
    position: "absolute",
    top: 10,
    right: 10,
}));

const Dialog = styled(MuiDialog)<DialogProps>(({ theme }) => ({
    "& .MuiDialog-paper": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "50%",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            width: "100%",
        },
    },
}));

const CreateUser: React.FC<Type_Props_Create_User> = ({ close }) => {
    const { t } = useTranslation();

    const { user } = useContext(SettingContext);

    const methods = useForm<Partial<Type_Create_User_Data>>({
        resolver: yupResolver(createUserSchema(t)),
        defaultValues: {
            email: user ? user.email : "",
            password: user ? user.password : "",
            confirmPassword: "",
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = methods;

    const token = sessionStorage.getItem("authToken");
    const { mutateAsync: mutateCreateUser } = mutationCreateUser(token || "");
    const { mutateAsync: mutateUpdateUser } = mutationUpdateUser(token || "");

    const queryClient = useQueryClient();

    const onSubmit = async (data: Partial<Type_Create_User_Data>) => {
        const { email, password } = data;

        if (!user && email && password)
            await mutateCreateUser({ email, password });
        else if (user) {
            const updatedData: Type_User = {
                ...user,
                email: data.email ?? user.email,
                password: data.password ?? user.password,
            };

            if (user.id) {
                await mutateUpdateUser({
                    userId: user.id,
                    body: updatedData,
                });
            }
        }

        close();
        queryClient.invalidateQueries(["users", token]);
    };

    return (
        <Dialog open onClose={close} fullWidth maxWidth="sm">
            <Icon disableRipple onClick={close} size="small">
                <FontAwesomeIcon icon={faTimes} />
            </Icon>

            <Box
                width="50%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
            >
                <Typography variant="h5">
                    {user ? t("user.update_user") : t("user.create_user")}
                </Typography>

                <FormProvider {...methods}>
                    <Box
                        component="form"
                        width="100%"
                        onSubmit={handleSubmit(onSubmit)}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        mb={3}
                    >
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    required={!user}
                                    label={t("login.email")}
                                    fullWidth
                                    size="small"
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            )}
                        />

                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    required={!user}
                                    label={t("login.password")}
                                    fullWidth
                                    size="small"
                                    type="password"
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                />
                            )}
                        />

                        <Controller
                            name="confirmPassword"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("user.confirm_password")}
                                    fullWidth
                                    size="small"
                                    type="password"
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword?.message}
                                />
                            )}
                        />

                        <Button
                            type="submit"
                            size="large"
                            variant="contained"
                            color="primary"
                            fullWidth
                        >
                            {t("validate")}
                        </Button>
                    </Box>
                </FormProvider>
            </Box>
        </Dialog>
    );
};

export default CreateUser;
